import type { Client, ClientStatus, Health, Plan } from './types';

/** Initials for the avatar, dropping the leading trade word. */
export function initials(name: string): string {
  const cleaned = name
    .replace(/^(Trattoria|Ristorante|Osteria|Hosteria|Pizzeria|Pasticceria|Macelleria|Fioraio|Barbiere|Forno|Caffè|Brasseria|Bar)\s+/i, '')
    .trim();
  const parts = cleaned.split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export const euro = (n: number): string => '€' + n.toLocaleString('it-IT');

/** "2026-06-12" → "12 giu 2026" */
const MONTHS = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
export function shortDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** Stato uptime di un sito → etichetta + spiegazione in italiano chiaro.
 *  Alimenta la scheda cliente: un "offline" deve SEMPRE dire il perché.
 *  Il check (apps/api .../uptime.ts) considera "up" qualsiasi risposta < 500;
 *  quindi offline = il server risponde 5xx, oppure non risponde affatto. */
export function uptimeInfo(
  isUp: boolean | null,
  httpStatus: number | null,
): { state: 'online' | 'offline' | 'unknown'; label: string; detail: string } {
  if (isUp == null) return { state: 'unknown', label: 'Mai controllato', detail: 'Il sito non è ancora stato controllato dal monitoraggio.' };
  if (isUp) return { state: 'online', label: 'Online', detail: httpStatus ? `Il server risponde (HTTP ${httpStatus}).` : 'Il server risponde.' };
  const detail =
    httpStatus == null
      ? 'Non raggiungibile: il sito non risponde affatto. Di solito è il DNS non puntato (dominio nuovo o record sbagliati), il server giù, o il certificato HTTPS non valido.'
      : httpStatus >= 500
        ? `Errore del server (HTTP ${httpStatus}): il dominio è puntato bene, ma l'applicazione è in errore o in fase di build.`
        : `Risposta inattesa (HTTP ${httpStatus}).`;
  return { state: 'offline', label: 'Offline', detail };
}

/** "adesso" / "3 minuti fa" / "2 ore fa" / "ieri" da un ISO timestamp — per l'ultimo controllo uptime. */
export function relFromNow(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'adesso';
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min} ${min === 1 ? 'minuto' : 'minuti'} fa`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} ${h === 1 ? 'ora' : 'ore'} fa`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'ieri';
  if (d < 30) return `${d} giorni fa`;
  const mo = Math.floor(d / 30);
  return `${mo} ${mo === 1 ? 'mese' : 'mesi'} fa`;
}

export const STATUS: Record<ClientStatus, { label: string; cls: string }> = {
  live: { label: 'Online', cls: 'ok' },
  draft: { label: 'Bozza', cls: 'draft' },
  onboarding: { label: 'Onboarding', cls: 'work' },
  paused: { label: 'Sospeso', cls: 'paused' },
  archived: { label: 'Archiviato', cls: 'paused' },
};

export const PLAN: Record<Plan, { label: string; cls: string }> = {
  base: { label: 'Base', cls: 'base' },
  completo: { label: 'Completo', cls: 'completo' },
};

export const HEALTH: Record<Health, { label: string; cls: string }> = {
  good: { label: 'In regola', cls: 'good' },
  attention: { label: 'Attenzione', cls: 'attention' },
  overdue: { label: 'In ritardo', cls: 'overdue' },
};

export interface Kpis {
  active: number;
  mrr: number;
  liveSites: number;
  onboarding: number;
  bookings30: number;
}

export function kpis(list: Client[]): Kpis {
  return {
    active: list.filter((c) => c.status === 'live').length,
    mrr: list.reduce((s, c) => s + c.mrr, 0),
    liveSites: list.filter((c) => c.status === 'live' && c.domainStatus === 'active').length,
    onboarding: list.filter((c) => c.status === 'onboarding' || c.status === 'draft').length,
    bookings30: list.reduce((s, c) => s + (c.bookings30 ?? 0), 0),
  };
}
